import { computed, ElementRef, Injectable, signal } from '@angular/core';

import {
  asyncScheduler,
  BehaviorSubject,
  distinctUntilChanged,
  EMPTY,
  fromEvent,
  map,
  of,
  pairwise,
  shareReplay,
  switchMap,
  throttleTime,
} from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { ToolbarState } from './view-page.service';

@Injectable()
export class ViewPageUIService {
  private readonly TOOLBAR_HEIGHT = 114;
  private readonly viewElementRef = signal<ElementRef<HTMLElement> | null>(null);
  private readonly untilDestroyed = injectUntilDestroyed();

  public toolbarState$ = new BehaviorSubject<ToolbarState>('visible');
  public toolbarAnimation$ = new BehaviorSubject<boolean>(false);
  public scrollElement$ = new BehaviorSubject<ElementRef<HTMLElement> | null>(null);

  private viewElRefClientHeight = computed(() => this.viewElementRef()?.nativeElement.clientHeight);
  private scroll$ = this.scrollElement$.pipe(
    map(elRef => elRef?.nativeElement),
    switchMap(element =>
      element
        ? fromEvent(element, 'scroll').pipe(
            map(() => ({
              scrollTop: element.scrollTop,
              scrollHeight: element.scrollHeight,
            })),
            pairwise(),
            throttleTime(16, asyncScheduler, { leading: false, trailing: true }),
          )
        : of(null),
    ),
    this.untilDestroyed,
    shareReplay(1),
  );

  constructor() {
    this.hideToolbarOnVerticalScroll();
  }

  public setViewElementRef(element: ElementRef<HTMLElement>) {
    this.viewElementRef.set(element);
  }

  public getToolbarAnimationHandler() {
    return {
      start: () => this.toolbarAnimation$.next(true),
      end: () => this.toolbarAnimation$.next(false),
    };
  }

  private hideToolbarOnVerticalScroll() {
    this.toolbarAnimation$
      .pipe(
        switchMap(isAnimating =>
          isAnimating
            ? EMPTY
            : this.scroll$.pipe(
                filterBoolean,
                map(([prev, current]) => {
                  const forceVisible =
                    current.scrollHeight <= this.viewElRefClientHeight()! ||
                    current.scrollTop <= this.TOOLBAR_HEIGHT;
                  if (forceVisible) return <const>'visible';

                  return prev.scrollTop > current.scrollTop ? <const>'scrolled' : <const>'hidden';
                }),
              ),
        ),
        distinctUntilChanged(),
        this.untilDestroyed,
      )
      .subscribe(isToolbarVisible => {
        this.toolbarState$.next(isToolbarVisible);
      });
  }
}
